import React from "react";
import { Link } from "react-router-dom";
import { PlayCircle, ExternalLink, ArrowRight } from "lucide-react";

const videos = [
  { slug: "rooted-coffee", title: "The 15-second product spotlight", client: "Rooted Coffee Roasters", tag: "Product spotlight", img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=800&q=80", stat: "+412% reach" },
  { slug: "north-and-main-barber", title: "Meet the hands behind the shop", client: "North & Main Barber Co.", tag: "Behind the scenes", img: "https://images.unsplash.com/photo-1621605815971-fbc98d665033?auto=format&fit=crop&w=800&q=80", stat: "+1.2k follows" },
  { slug: "hazel-and-hem", title: "A bride-to-be says it best", client: "Hazel &amp; Hem Bridal", tag: "Customer story", img: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=800&q=80", stat: "9 bookings in 7 days" },
];

const sites = [
  { slug: "north-and-main-site", title: "Fresh cuts. Easy booking.", client: "North & Main Barber Co.", tag: "Service business", img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80", url: "northandmain.co", kpi: "3x more online bookings" },
  { slug: "hazel-and-hem-shop", title: "Shop the collection", client: "Hazel &amp; Hem Bridal", tag: "Online store", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80", url: "hazelandhem.shop", kpi: "$14k first-month sales" },
  { slug: "lamplight-candle", title: "Faith-rooted candles, hand-poured", client: "Lamplight Candle Co.", tag: "Faith / lifestyle brand", img: "https://images.unsplash.com/photo-1602874801007-aa70d2dd5848?auto=format&fit=crop&w=1200&q=80", url: "lamplightcandle.co", kpi: "42% returning customers" },
];

export default function PortfolioSection() {
  return (
    <section id="portfolio" className="scroll-mt-24">
      {/* Video work */}
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
        <div className="text-center">
          <p className="kicker">Portfolio · Videos</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Real work, real results</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">A few recent videos we've made for businesses just like yours. Tap any case to read the full story.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {videos.map((v, i) => (
            <Link key={i} to={`/portfolio/${v.slug}`} className="group relative rounded-2xl overflow-hidden bg-[color:var(--dms-navy)] aspect-[9/14] block" data-testid={`pf-video-${i}`}>
              <img src={v.img} alt={v.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-85 transition" />
              <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--dms-navy)]/95 via-[color:var(--dms-navy)]/35 to-transparent" />

              <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] tracking-widest uppercase text-white font-semibold">
                {v.tag}
              </div>
              <div className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center shadow-lg">
                <PlayCircle className="w-5 h-5 text-[color:var(--dms-navy)]" />
              </div>

              <div className="absolute left-0 right-0 bottom-0 p-6 text-white">
                <p className="font-serif text-xl leading-snug" dangerouslySetInnerHTML={{ __html: v.title }} />
                <p className="mt-2 text-xs text-white/70" dangerouslySetInnerHTML={{ __html: v.client }} />
                <div className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-[color:var(--dms-peach)] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--dms-peach)]" /> {v.stat}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Website work */}
      <div className="bg-[color:var(--dms-cream-2)]/50 py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center">
            <p className="kicker">Portfolio · Websites</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Websites we've shipped</h2>
            <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
            <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Clean pages, strong photos and a clear next step on every screen.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {sites.map((s, i) => (
              <Link key={i} to={`/portfolio/${s.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 hover-lift block" data-testid={`pf-site-${i}`}>
                <div className="relative aspect-[4/3] bg-[color:var(--dms-cream-2)] overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] tracking-widest uppercase text-[color:var(--dms-navy)] font-semibold">
                    {s.tag}
                  </div>
                </div>
                <div className="p-6">
                  <p className="font-serif text-xl text-[color:var(--dms-navy)]" dangerouslySetInnerHTML={{ __html: s.title }} />
                  <p className="mt-1 text-sm text-[color:var(--dms-navy)]/60" dangerouslySetInnerHTML={{ __html: s.client }} />
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-[color:var(--dms-navy)]/10">
                    <span className="inline-flex items-center gap-1.5 text-xs text-[color:var(--dms-navy)]/70 font-mono italic">
                      <ExternalLink className="w-3.5 h-3.5" /> {s.url}
                    </span>
                    <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-peach)] font-semibold">{s.kpi}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="#intake" className="btn-ghost-navy" data-testid="pf-cta">Start your project <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </div>
    </section>
  );
}
